import _ from 'lodash';
import { apolloClient } from '@/vue-apollo';
import { lsApiService, lsErrorService, lsTrackingService } from 'ls-vue-common';
import {
  REACTIVATION__RESET,
  REACTIVATION__DISAGREE_WITH_POLICIES,
  REACTIVATION__GET_PRICING_AND_SELLABLE_DAYS,
  REACTIVATION__GET_SELLABLE_DAYS,
  REACTIVATION__MUTATE_AVAILABLE_CYCLES,
  REACTIVATION__MUTATE_DEAL,
  REACTIVATION__MUTATE_LOADING_STATUS,
  REACTIVATION__MUTATE_SELLABLE_DAYS,
  REACTIVATION__REACTIVATE_SERVICE,
  REACTIVATION__VALIDATE_DEAL,
  REACTIVATION__UNSELECTABLE_DAYS,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  AUTH__MUTATE_CUSTOMER,
} from '@/store/types';
import { lsModuleHelpers } from '../helpers';
import propertySellableDaysQuery from '@/graphql/queries/propertySellableDays.gql';

const createInitialState = () => {
  return {
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
    sellableDays: {
      sunday: false,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
    },
    availableCycles: [],
    validDeal: {},
  };
};

export const mutations = {
  [REACTIVATION__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },
  [REACTIVATION__MUTATE_AVAILABLE_CYCLES](state, payload) {
    _.assign(state.availableCycles, payload.availableCycles);
  },
  [REACTIVATION__MUTATE_SELLABLE_DAYS](state, payload) {
    _.assign(state.sellableDays, payload.sellableDays);
  },
  [REACTIVATION__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
  [REACTIVATION__MUTATE_DEAL](state, payload) {
    _.assign(state.validDeal, payload);
  },
};

export const actions = {
  [REACTIVATION__GET_PRICING_AND_SELLABLE_DAYS]({ commit, rootState }, { property_id, deal_id }) {
    const { customer_id } = rootState.auth.user;

    commit(REACTIVATION__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const dealIdParam = deal_id ? `&deal_id=${deal_id}` : '';

    const prices = lsApiService.request({
      method: 'GET',
      route: `/customers/${customer_id}/properties/${property_id}/prices?service_id=1${dealIdParam}`,
    });

    const sellableDays = lsApiService.request({
      method: 'GET',
      route: `/customers/${customer_id}/properties/${property_id}/sellable-days`,
    });

    return Promise.all([prices, sellableDays])
      .then((response) => {
        if (_.isEmpty(response[0].data.prices)) {
          const error = {
            message: `property_id ${property_id} is marked can_self_reactivate but has no prices available`,
          };
          lsErrorService.log({ error, level: 'warn' });
        }

        // ensure response is sorted by lowest cycle first
        response[0].data.prices = _.sortBy(response[0].data.prices, 'cycle');

        commit(REACTIVATION__MUTATE_AVAILABLE_CYCLES, {
          availableCycles: response[0].data.prices,
        });
        commit(REACTIVATION__MUTATE_SELLABLE_DAYS, {
          sellableDays: response[1].data,
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(REACTIVATION__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },
  [REACTIVATION__GET_SELLABLE_DAYS]({ commit, rootState }, { property_id }) {
    const { customer_id } = rootState.auth.user;

    commit(REACTIVATION__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    apolloClient
      .query({
        query: propertySellableDaysQuery,
        variables: {
          customer_id: customer_id,
          property_id: property_id,
        },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        commit(REACTIVATION__MUTATE_SELLABLE_DAYS, {
          sellableDays: data.propertySellableDays,
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(REACTIVATION__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },
  [REACTIVATION__REACTIVATE_SERVICE](
    { commit, dispatch, rootState },
    { property_id, start_date, cycle, onSuccess, deal = undefined }
  ) {
    const { customer_id } = rootState.auth.user;

    commit(REACTIVATION__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/reactivate`,
        payload: {
          start_date,
          cycle,
          deal,
        },
      })
      .then(() => {
        dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, {
          forceRefresh: true,
        });

        commit(AUTH__MUTATE_CUSTOMER, {
          is_customer_lead: false,
        });

        // growth requested this exact tracking information be sent
        lsTrackingService.track({
          target: 'reactivation-form',
          event: 'CompletedReactivation',
          data: {
            revenue: 0.01,
          },
        });
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(REACTIVATION__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [REACTIVATION__DISAGREE_WITH_POLICIES](
    { commit, dispatch, rootState },
    { property_id, onSuccess = _.noop }
  ) {
    const { customer_id } = rootState.auth.user;

    commit(REACTIVATION__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        apiVersion: 'v2',
        route: `/customers/${customer_id}/cancel`,
        payload: {
          properties: [
            {
              property_id,
              reason: 'Bad Fit, Policy Disagreement',
            },
          ],
        },
      })
      .then(() => {
        dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, {
          forceRefresh: true,
        });
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(REACTIVATION__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [REACTIVATION__VALIDATE_DEAL]({ commit }, { deal }) {
    commit(REACTIVATION__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });
    return lsApiService
      .request({
        method: 'GET',
        route: `/deals/${deal}`,
      })
      .then(({ data }) => {
        commit(REACTIVATION__MUTATE_DEAL, data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(REACTIVATION__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },
};

export const getters = {
  [REACTIVATION__UNSELECTABLE_DAYS](state) {
    const { sellableDays } = state;
    const arrayOfDays = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];

    return _.reduce(
      arrayOfDays,
      (prev, curr, idx) => {
        if (!sellableDays[curr]) {
          prev.push(idx);
        }
        return prev;
      },
      []
    );
  },
};

export const reactivation = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
